import { useCallback, useState, useTransition } from 'react'

import styled, { css } from 'styled-components'

import { desktopCSS } from '@measures/responsive'
import translate from '@i18n'
import useAuthentication from '@hooks/useAuthentication'
import { getFmtCompetitionEntityIdsSorted } from '@hooks/useFmtCompetitionEntityIdsSorted'
import useTracking, { TrackingFnType } from '@hooks/useTracking'
import useUser from '@hooks/useUser'
import useViewportTracking from '@hooks/useViewportTracking'
import FmTOnboardingContainer from '@components/FmtOnboardingContainer/FmTOnboardingContainer'
import FmtTeamFilter from '@components/FmtTeamFilter'
import { StyledStickyContainerWrapper } from '@components/FollowMyTeam/FollowMyTeamMyTeams'
import Searchbar from '@components/FollowMyTeam/Searchbar'
import SearchbarContainer from '@components/FollowMyTeam/Searchbar/SearchbarContainer'

import FollowMyTeamSportCompetitorsList from './FollowMyTeamSportCompetitorsList'

const Container = styled.div`
  ${({
    theme: {
      spacing: { spacing16 },
    },
  }) => css`
    display: flex;
    flex-direction: column;
    > * {
      padding-left: ${spacing16};
      padding-right: ${spacing16};
    }
  `}
`

const LoginMessage = styled.div`
  ${({
    theme: {
      colors: { textDefault },
      typography: {
        catchword: {
          medium2: { bundledCSS: catchwordMedium2CSS },
        },
      },
    },
  }) => css`
    color: ${textDefault};
    ${catchwordMedium2CSS}
  `}
`

const StyledLink = styled.span`
  text-decoration: underline;
  cursor: pointer;
`

const FmtCompetitorsStyledWrapper = styled.div<{
  showFilters?: boolean
  showFmtOnboardingContainer?: boolean
}>`
  ${({
    theme: {
      spacing: { spacing32 },
    },
    showFmtOnboardingContainer,
    showFilters,
  }) => css`
    display: flex;
    flex-direction: column;
    gap: ${spacing32};
    overflow-y: auto;
    max-height: calc(
      100dvh -
        ${showFmtOnboardingContainer && showFilters
          ? '330px'
          : showFmtOnboardingContainer
            ? '266px'
            : showFilters
              ? '184px'
              : '120px'}
    );
    max-height: calc(
      100vh -
        ${showFmtOnboardingContainer && showFilters
          ? '330px'
          : showFmtOnboardingContainer
            ? '266px'
            : showFilters
              ? '184px'
              : '120px'}
    );

    ${desktopCSS(css`
      max-height: calc(
        100dvh -
          ${showFmtOnboardingContainer && showFilters
            ? '390px'
            : showFmtOnboardingContainer
              ? '268px'
              : showFilters
                ? '184px'
                : '120px'}
      );
      max-height: calc(
        100vh -
          ${showFmtOnboardingContainer && showFilters
            ? '390px'
            : showFmtOnboardingContainer
              ? '268px'
              : showFilters
                ? '184px'
                : '120px'}
      );
    `)};
  `}
`

export const StyledCompetitorsPlaceholder = styled.div`
  ${({
    theme: {
      spacing: { spacing24 },
    },
  }) => css`
    margin: ${spacing24} 0;
    display: flex;
    flex-direction: column;
    text-align: center;
  `}
`

export const PlaceholderTitle = styled.div`
  ${({
    theme: {
      typography: {
        subheads: {
          large1: { bundledCSS: large1Subhead },
        },
      },
      colors: { textDefault },
    },
  }) => css`
    ${large1Subhead}
    color: ${textDefault};
  `}
`

export const PlaceholderText = styled.div`
  ${({
    theme: {
      typography: {
        bodycopy: {
          small2: { bundledCSS: bodycopySmall2 },
        },
      },
      colors: { textDefault },
    },
  }) => css`
    ${bodycopySmall2}
    color: ${textDefault};
  `}
`

const FollowMyTeamSelectTeam = ({ onClose }: { onClose: () => void }) => {
  const user = useUser()
  const { login } = useAuthentication()
  const [showFmtOnboardingContainer, setShowFmtOnboardingContainer] =
    useState<boolean>(true)
  const [currentSearchTerm, setCurrentSearchTerm] = useState('')
  const [, startTransition] = useTransition()
  const [selectedFilterId, setSelectedFilterId] = useState<string>('')

  const onSearchTermUpdate = useCallback((searchTerm: string) => {
    setSelectedFilterId('')
    startTransition(() => {
      setCurrentSearchTerm(searchTerm)
    })
  }, [])

  const onLogin = () => login({ prefix: 'FMT', loginCase: 'email_only' })

  const onImpressionEvent = useCallback<TrackingFnType>(
    ({ queryClient }) => ({
      event: 'select_team_impression',
      element: getFmtCompetitionEntityIdsSorted(queryClient)?.length ?? 0,
    }),
    [],
  )

  const trackedOnImpression = useTracking(onImpressionEvent)

  const viewportRef = useViewportTracking({
    track: true,
    onImpression: trackedOnImpression,
  })

  return (
    <Container ref={viewportRef}>
      <StyledStickyContainerWrapper>
        <SearchbarContainer onClose={onClose}>
          <Searchbar onSearchTermUpdate={onSearchTermUpdate} />
        </SearchbarContainer>
        <FmTOnboardingContainer
          position="selectTeam"
          setShowFmtOnboardingContainer={setShowFmtOnboardingContainer}
          showFmtOnboardingContainer={showFmtOnboardingContainer}
        />
        {!user && (
          <LoginMessage>
            {translate('followMyTeam.alreadyAdded')}{' '}
            <StyledLink onClick={onLogin}>
              {translate('followMyTeam.login')}
            </StyledLink>
          </LoginMessage>
        )}
        <FmtTeamFilter
          setSelectedFilterId={setSelectedFilterId}
          selectedFilterId={selectedFilterId}
          currentSearchTerm={currentSearchTerm}
        />
      </StyledStickyContainerWrapper>
      <FmtCompetitorsStyledWrapper
        showFilters={!currentSearchTerm}
        showFmtOnboardingContainer={showFmtOnboardingContainer}
      >
        <FollowMyTeamSportCompetitorsList
          currentSearchTerm={currentSearchTerm}
          currentFilterId={selectedFilterId}
        />
      </FmtCompetitorsStyledWrapper>
    </Container>
  )
}

export default FollowMyTeamSelectTeam
